import { useCallback } from 'react'
import BackofficePanel, { PanelError, PanelNote, Row } from '../BackofficePanel'
import { detailFor, relativeTime, titleFor } from '../feedText'
import { useAdminPoll } from '../../../app/backoffice/useAdminPoll'
import { countdown, displayMinute, useMatchClock } from '../../../app/backoffice/useMatchClock'
import { getAdminMatches } from '../../../api/admin'
import type { AdminMatchesWithEvents, FeedEventView, MatchView } from '../../../adminApi'

/**
 * The live matches panel.
 *
 * Four questions, in the order they get asked on a matchday: what is being played right now and at
 * what score; what kicks off next; when is the deadline; and is anything wrong with one of these.
 *
 * Polls on the same cadence as `useLivePoll` — it costs us nothing and the provider nothing, since
 * this reads our own tables.
 */

const MATCHES_POLL_MS = 10_000

export default function LiveMatchesPanel({
  enabled,
  onCorrect,
}: {
  enabled: boolean
  /** Hands off to the override sheet. This panel is a view; it edits nothing itself. */
  onCorrect: (match: MatchView) => void
}) {
  const load = useCallback(() => getAdminMatches(), [])
  const poll = useAdminPoll(load, { enabled, intervalMs: MATCHES_POLL_MS })
  const { elapsedMs } = useMatchClock(poll.data?.serverTime ?? null)
  const data = poll.data

  return (
    <BackofficePanel
      title="Live matches"
      badge={data ? `${data.live.length} in play` : '…'}
      subtitle={
        data
          ? `synced ${data.lastLiveSyncAt ? relativeTime(data.lastLiveSyncAt, Date.parse(data.serverTime) + elapsedMs) : 'never'}${
              data.stale ? ' — the live poll is behind' : ''
            }`
          : undefined
      }
    >
      {poll.error !== null && <PanelError error={poll.error} />}
      {data && (
        <>
          {data.live.length === 0 ? (
            <EmptyState data={data} elapsedMs={elapsedMs} />
          ) : (
            data.live.map((match) => (
              <LiveMatchCard
                key={match.id}
                match={match}
                events={data.eventsByFixture[match.id] ?? []}
                nowMs={Date.parse(data.serverTime) + elapsedMs}
                elapsedMs={elapsedMs}
                onCorrect={onCorrect}
              />
            ))
          )}

          {data.gameweek !== null && (
            <div className="bo-group">
              <p className="bo-group__title">GW{data.gameweek} deadline</p>
              {/* Never computed client-side: the server owns what a deadline means, and the countdown
                  runs off `serverTime`, not `Date.now()`. */}
              <Row
                label={data.deadlineUtc ?? 'no deadline'}
                value={
                  data.deadlineUtc === null
                    ? '—'
                    : data.deadlinePassed
                      ? 'passed'
                      : countdown(data.deadlineUtc, data.serverTime, elapsedMs)
                }
              />
            </div>
          )}

          <div className="bo-group">
            <p className="bo-group__title">Next up</p>
            {data.upcoming.length === 0 ? (
              <p className="bo-empty">Nothing scheduled ahead of now.</p>
            ) : (
              data.upcoming.map((match) => (
                <Row
                  key={match.id}
                  label={`${match.homeTeamAbbr} v ${match.awayTeamAbbr}`}
                  value={`GW${match.gameweek} · ${countdown(match.kickoffUtc, data.serverTime, elapsedMs)}`}
                />
              ))
            )}
          </div>

          <div className="bo-group">
            <p className="bo-group__title">Just settled</p>
            {data.recent.length === 0 ? (
              <p className="bo-empty">Nothing finished in the last 24 hours.</p>
            ) : (
              data.recent.map((match) => (
                <Row
                  key={match.id}
                  label={`${match.homeTeamAbbr} v ${match.awayTeamAbbr}`}
                  value={`${match.homeScore ?? '-'}–${match.awayScore ?? '-'}${match.overridden ? ' (manual)' : ''}`}
                />
              ))
            )}
          </div>
        </>
      )}
    </BackofficePanel>
  )
}

/**
 * No matches in play is the normal condition six days a week, so this says what *is* happening
 * rather than rendering a blank. The live window's own verdict is shown verbatim: "window closed:
 * no fixture within 15 minutes" is a diagnosis; an empty box is not.
 */
function EmptyState({ data, elapsedMs }: { data: AdminMatchesWithEvents; elapsedMs: number }) {
  const next = data.upcoming[0]
  return (
    <>
      <p className="bo-empty">
        No matches in play.
        {next &&
          ` Next: ${next.homeTeamName} v ${next.awayTeamName}, ${new Date(next.kickoffUtc).toUTCString()} (${countdown(next.kickoffUtc, data.serverTime, elapsedMs)})`}
      </p>
      {data.liveWindowReason !== null && (
        <Row
          label={data.liveWindowOpen ? 'window open' : 'window closed'}
          value={data.liveWindowReason}
        />
      )}
      {data.modeNote !== null && <PanelNote>{data.modeNote}</PanelNote>}
    </>
  )
}

function LiveMatchCard({
  match,
  events,
  nowMs,
  elapsedMs,
  onCorrect,
}: {
  match: MatchView
  events: FeedEventView[]
  nowMs: number
  elapsedMs: number
  onCorrect: (match: MatchView) => void
}) {
  return (
    <div className="bo-match">
      <div className="bo-match__score">
        {match.status === 'live' && <span className="bo-match__pulse" aria-hidden="true" />}
        <span>
          {match.homeTeamAbbr} {match.homeScore ?? 0} – {match.awayScore ?? 0} {match.awayTeamAbbr}
        </span>
        <span className="bo-match__minute">{displayMinute(match.status, match.minute, elapsedMs)}</span>
        {/* Loud on purpose: `unknown` means a status mapping failed, and the feed emits a
            `status-unknown` event for exactly the same reason. */}
        {match.status === 'unknown' && <span className="bo-tag bo-tag--error">unknown status</span>}
        {match.overridden && <span className="bo-tag bo-tag--manual">override</span>}
      </div>

      <div className="bo-match__meta">
        <span>
          synced {match.lastSyncedAt ? relativeTime(match.lastSyncedAt, nowMs) : 'never'}
        </span>
        {/* Exactly the stuck-fixture condition — the single most likely thing to be wrong. */}
        {match.stuck && (
          <span className="bo-tag bo-tag--error">
            in play longer than a match can run — stuck
          </span>
        )}
        <span className="bo-mono">{match.id}</span>
      </div>

      {events.length > 0 && (
        <ul className="bo-match__events">
          {events.map((event) => (
            <li key={event.id}>
              {titleFor(event)}
              {detailFor(event) && ` — ${detailFor(event)}`}
            </li>
          ))}
        </ul>
      )}

      <div className="bo-btn-row">
        <button type="button" className="bo-btn" onClick={() => onCorrect(match)}>
          Correct this match
        </button>
      </div>
    </div>
  )
}
